import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, UserModel } from '../models/User';
import { AuthService } from '../services/AuthService';
import { PushService } from '../services/PushService';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isGuest: boolean;
  continueAsGuest: () => void;
  exitGuestMode: () => void;
  isAdmin: boolean;
  hasValidAccess: boolean;
  hasActiveSubscription: boolean;
  trialDaysRemaining: number;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string, fullName?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(() => localStorage.getItem('2ez_guest') === '1');

  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged((u, loading) => {
      setUser(u);
      setIsLoading(loading);
      if (u) PushService.registerForUser(u.id);
    });
    return unsubscribe;
  }, []);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    isGuest,
    continueAsGuest: () => {
      localStorage.setItem('2ez_guest', '1');
      setIsGuest(true);
    },
    exitGuestMode: () => {
      localStorage.removeItem('2ez_guest');
      setIsGuest(false);
    },
    isAdmin: UserModel.isAdmin(user),
    hasValidAccess: UserModel.hasValidAccess(user),
    hasActiveSubscription: UserModel.hasActiveSubscription(user),
    trialDaysRemaining: UserModel.trialDaysRemaining(user),
    signIn: async (email, password) => {
      const u = await AuthService.signIn(email, password);
      localStorage.removeItem('2ez_guest');
      setIsGuest(false);
      setUser(u);
    },
    signUp: async (email, password, displayName, fullName) => {
      const u = await AuthService.signUp(email, password, displayName, fullName);
      localStorage.removeItem('2ez_guest');
      setIsGuest(false);
      setUser(u);
    },
    signOut: async () => {
      await AuthService.signOut();
      await PushService.unregister();
      setUser(null);
    },
    refreshUser: async () => {
      if (user) {
        const u = await AuthService.loadUserData(user.id);
        setUser(u);
      }
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
